import { Button, Flex, Typography, Switch, InputNumber, Select, Table, Tag, message, Alert } from 'antd';
import { useRecoilValue } from 'recoil';
import { useEffect, useState } from 'react';
import { themeState } from '../../atom';
import CustomCard from '../../components/Card';
import ApiService from '../../APIServices/ApiService';

const { Title, Text } = Typography;

/**
 * "Buy X, get Y free" — the cross-product offers advertised on the storefront.
 *
 * These used to be hardcoded in youtra-25 (a PROMO_MIN constant and a function
 * that could only return ONE free product), so changing an offer meant a deploy
 * and an eSIM could not give away both a guide and a language guide.
 *
 * The three trigger rows are fixed — they are the three things the shop sells —
 * so this is a form, not a CRUD list. Saving sends all three at once.
 */

const TRIGGER_LABELS = {
    book: 'Travel guide',
    language_guide: 'Language guide',
    esim: 'eSIM',
};

const GRANT_OPTIONS = [
    { value: 'book', label: 'Travel guide' },
    { value: 'language_guide', label: 'Language guide' },
];

const CompanionOffers = () => {
    const theme = useRecoilValue(themeState);
    const [offers, setOffers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        fetchOffers();
    }, []);

    const fetchOffers = async () => {
        setLoading(true);
        try {
            const res = await ApiService.getCompanionOffers();
            setOffers(res?.offers || []);
        } catch (error) {
            message.error(error?.response?.data?.message || 'Failed to load offers.');
            setOffers([]);
        } finally {
            setLoading(false);
        }
    };

    /** Patch one row in place; the table is driven straight off this state. */
    const patch = (trigger, changes) => {
        setOffers((prev) => prev.map((o) => (o.trigger === trigger ? { ...o, ...changes } : o)));
    };

    const save = async () => {
        // An enabled rule that grants nothing would advertise a gift and hand
        // over nothing — refuse it here rather than let the storefront print it.
        const empty = offers.find((o) => o.enabled && (!o.grants || o.grants.length === 0));
        if (empty) {
            message.error(`"${TRIGGER_LABELS[empty.trigger]}" is switched on but gives nothing away.`);
            return;
        }

        setSaving(true);
        try {
            const res = await ApiService.updateCompanionOffers(offers);
            setOffers(res?.offers || offers);
            message.success('Offers saved.');
        } catch (error) {
            message.error(error?.response?.data?.message || 'Failed to save offers.');
        } finally {
            setSaving(false);
        }
    };

    const columns = [
        {
            title: 'When the cart contains',
            dataIndex: 'trigger',
            key: 'trigger',
            render: (val) => <Tag color="blue">{TRIGGER_LABELS[val] || val}</Tag>,
        },
        {
            title: 'Give away free',
            key: 'grants',
            render: (_, record) => (
                <Select
                    mode="multiple"
                    allowClear
                    style={{ minWidth: 260 }}
                    placeholder="Nothing"
                    value={record.grants || []}
                    options={GRANT_OPTIONS}
                    onChange={(v) => patch(record.trigger, { grants: v })}
                />
            ),
        },
        {
            title: 'Minimum spend (€)',
            key: 'minOrderAmount',
            render: (_, record) => (
                <InputNumber
                    min={0}
                    step={1}
                    style={{ width: 140 }}
                    placeholder="No minimum"
                    value={record.minOrderAmount}
                    onChange={(v) => patch(record.trigger, { minOrderAmount: v ?? 0 })}
                />
            ),
        },
        {
            title: 'Active',
            key: 'enabled',
            render: (_, record) => (
                <Switch
                    checked={record.enabled !== false}
                    onChange={(v) => patch(record.trigger, { enabled: v })}
                />
            ),
        },
    ];

    return (
        <CustomCard theme={theme}>
            <Flex justify="space-between" align="flex-start" wrap="wrap" gap={16}>
                <div>
                    <Title level={4} style={{ margin: 0 }}>Free companion products</Title>
                    <Text type="secondary">
                        Buy one thing, get another free. Shown as a badge on the storefront and
                        honoured in the cart.
                    </Text>
                </div>
                <Button type="primary" loading={saving} onClick={save}>Save changes</Button>
            </Flex>

            <Alert
                style={{ marginTop: 16 }}
                type="info"
                showIcon
                message="Minimum spend is per rule"
                description="The storefront advertises the eSIM bundle at €10+ while the guide offers have no floor. Set 0 for no minimum. The free product is chosen by the customer in the cart and granted after payment."
            />

            <Table
                style={{ marginTop: 16 }}
                rowKey="trigger"
                columns={columns}
                dataSource={offers}
                loading={loading}
                pagination={false}
            />
        </CustomCard>
    );
};

export default CompanionOffers;
